import {CorePortalContactService, CorePortalDashboardConfig, CorePortalFunctionService} from '@app/core-portal';
import {AppEntityType, ControllerOperationId} from '@app/core-shared';
import {TechPortalTicketService} from '@app/tech-portal-lib';
import {TechPortalFeatureTicketSettingsEscalationLevelService} from '@app/tech-portal/features/ticket-settings/features/escalation-levels';
import {Injector} from '@angular/core';
import {TechPortalFeatureTicketSettingsStateService} from '@app/tech-portal/features/ticket-settings/features/states';
import {
  TechPortalFeatureResourceListComponent,
  TechPortalFeatureResourceMissionService,
  TechPortalFeatureResourceService,
  TechPortalFeatureResourceTicketService,
  TechPortalFeatureVirtualResourceService
} from '@app/tech-portal/features/resources';
import {
  CorePortalFeatureMasterDataLocationListComponent,
  CorePortalFeatureMasterDataLocationService
} from '@app/core-portal/features/master-data/features/locations';
import {CorePortalFeatureMasterDataLocationGroupService} from '@app/core-portal/features/master-data/features/location-groups';
import {
  TechPortalFeatureDashboardMissionActionsFacade,
  TechPortalFeatureDashboardMissionDatatableStore,
  TechPortalFeatureMissionListComponent,
  TechPortalFeatureMissionReportsListService,
  TechPortalFeatureMissionService
} from '@app/tech-portal/features/missions';
import {CorePortalFeatureResourceService} from '@app/core-portal/features/resources';
import {TechPortalFeatureResourceTaskService} from '@app/tech-portal/features/resource-tasks';
import {
  TechPortalFeatureContractListComponent,
  TechPortalFeatureContractService
} from '@app/tech-portal/features/contracts';
import {
  CorePortalFeatureMasterDataCompanyListComponent,
  CorePortalFeatureMasterDataCompanyService
} from '@app/core-portal/features/master-data/features/companies';
import {
  TechPortalFeatureDashboardTicketActionsFacade,
  TechPortalFeatureDashboardTicketDatatableStore,
  TechPortalFeatureTicketListComponent
} from '@app/tech-portal/features/tickets';
import {
  CorePortalFeatureMasterDataContactListComponent,
  CorePortalFeatureMasterDataContactService
} from '@app/core-portal/features/master-data/features/contacts';
import {
  TechPortalFeatureIssueListComponent,
  TechPortalFeatureIssueListService
} from "@app/tech-portal/features/issues";
import {
  DirectMessageIssueService,
  MessageControlIssueService,
  MessageIssueService,
  MissionIssueService,
  OrderIssueService,
  TicketIssueService
} from "@app/tech-portal/features/issues/src/lib/store";
import {
  TechPortalFeatureDirectMessageIssueListComponent,
  TechPortalFeatureMessageControlIssueListComponent,
  TechPortalFeatureMessageIssueListComponent,
  TechPortalFeatureMissionIssueListComponent,
  TechPortalFeatureOrderIssueListComponent,
  TechPortalFeatureTicketIssueListComponent
} from "@app/tech-portal/features/issues/src/lib/containers";
import {LocationTicketListComponent} from "@app/core-portal/features/master-data/features/locations/src/lib/containers";
import {LocationTicketService} from "@app/core-portal/features/master-data/features/locations/src/lib/store";
import {
  ConnectedOccurredConsumptionsListComponent,
  ConnectedOccurredErrorsListComponent,
  ConnectedOccurredHintsListComponent,
  ResourceControlProtocolConsumptionsComponent,
  ResourceControlProtocolErrorsComponent,
  ResourceMissionsComponent,
  ResourceTicketsComponent
} from "@app/tech-portal/features/resources/src/lib/containers";
import {
  TechPortalFeatureConnectedOccurredConsumptionsService,
  TechPortalFeatureConnectedOccurredErrorsService,
  TechPortalFeatureConnectedOccurredHintsService,
  TechPortalFeatureResourceControlProtocolConsumptionsService,
  TechPortalFeatureResourceControlProtocolErrorsService,
} from "@app/tech-portal/features/resources/src/lib/store";
import {
  PathsProgressContactService,
  PathsProgressFunctionService,
  PathsProgressLocationService
} from "@app/orga-portal/features/knowledge/paths/src/lib/store";
import {
  PathsProgressContactListComponent,
  PathsProgressFunctionListComponent,
  PathsProgressLocationListComponent
} from "@app/orga-portal/features/knowledge/paths/src/lib/containers";
import {ModelListComponent} from "@app/core-portal/features/settings/features/manufacturers/src/lib/containers";
import {CorePortalManufacturerModelService} from "@app/core-portal/features/settings/features/manufacturers";
import {
  MissionMyRequestsComponent,
  MissionReportsListComponent,
  TechPortalFeatureMissionGanttViewComponent,
  TechPortalFeatureMissionReceiptsListComponent
} from "@app/tech-portal/features/missions/src/lib/containers";
import {
  TechPortalFeatureMissionMyRequestsService,
  TechPortalFeatureMissionReceiptsListService
} from "@app/tech-portal/features/missions/src/lib/store";
import {MissionMapViewComponent} from "@app/tech-portal/features/missions/src/lib/components";

export function provideDashboardConfig(): CorePortalDashboardConfig {
  return {
    [ControllerOperationId.TicketControllerList]: {
      listComponent: TechPortalFeatureTicketListComponent,
      datatableStore: TechPortalFeatureDashboardTicketDatatableStore,
      actionsFacade: TechPortalFeatureDashboardTicketActionsFacade,
      serializedName: 'TicketListDto',
      idKey: 'ticketId',
      entityService: TechPortalTicketService,
      entityType: AppEntityType.Ticket,
      detailLink: '/tech-dashboard/tickets',
      module: 'management',
      providers: [
        {
          provide: TechPortalFeatureTicketSettingsEscalationLevelService,
          useClass: TechPortalFeatureTicketSettingsEscalationLevelService,
          deps: [Injector]
        },
        {
          provide: TechPortalFeatureTicketSettingsStateService,
          useClass: TechPortalFeatureTicketSettingsStateService,
          deps: [Injector]
        },
        {
          provide: TechPortalFeatureResourceService,
          useClass: TechPortalFeatureResourceService,
          deps: [Injector]
        },
        {
          provide: CorePortalContactService,
          useClass: CorePortalContactService,
          deps: [Injector]
        },
        {
          provide: CorePortalFunctionService,
          useClass: CorePortalFunctionService,
          deps: [Injector]
        },
        {
          provide: CorePortalFeatureMasterDataLocationService,
          useClass: CorePortalFeatureMasterDataLocationService,
          deps: [Injector]
        },
        {
          provide: CorePortalFeatureMasterDataLocationGroupService,
          useClass: CorePortalFeatureMasterDataLocationGroupService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.ContactControllerList]: {
      listComponent: CorePortalFeatureMasterDataContactListComponent,
      serializedName: 'ContactListDto',
      idKey: 'contactId',
      entityService: CorePortalContactService,
      entityType: AppEntityType.Contact,
      detailLink: '/tech-dashboard/contacts',
      module: 'management',
      providers: [
        {
          provide: CorePortalContactService,
          useClass: CorePortalContactService,
          deps: [Injector]
        },
        {
          provide: CorePortalFeatureMasterDataContactService,
          useClass: CorePortalFeatureMasterDataContactService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.IssueControllerId]: {
      listComponent: TechPortalFeatureIssueListComponent,
      serializedName: 'IssueListDto',
      idKey: 'issueId',
      entityService: TechPortalFeatureIssueListService,
      entityType: AppEntityType.Issue,
      detailLink: null,
      module: null,
      providers: [
        {
          provide: TechPortalFeatureIssueListService,
          useClass: TechPortalFeatureIssueListService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.TicketIssueControllerId]: {
      listComponent: TechPortalFeatureTicketIssueListComponent,
      serializedName: 'TicketIssueListDto',
      idKey: 'issueId',
      entityService: TicketIssueService,
      entityType: AppEntityType.Issue,
      detailLink: '/issues/ticket',
      module: 'communication',
      providers: [
        {
          provide: TicketIssueService,
          useClass: TicketIssueService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.MessageIssueControllerId]: {
      listComponent: TechPortalFeatureMessageIssueListComponent,
      serializedName: 'MessageIssueListDto',
      idKey: 'issueId',
      entityService: MessageIssueService,
      entityType: AppEntityType.Issue,
      detailLink: '/issues/message',
      module: 'communication',
      providers: [
        {
          provide: MessageIssueService,
          useClass: MessageIssueService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.MissionIssueControllerId]: {
      listComponent: TechPortalFeatureMissionIssueListComponent,
      serializedName: 'MissionIssueListDto',
      idKey: 'issueId',
      entityService: MissionIssueService,
      entityType: AppEntityType.Issue,
      detailLink: '/issues/mission',
      module: 'communication',
      providers: [
        {
          provide: MissionIssueService,
          useClass: MissionIssueService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.OrderIssueControllerId]: {
      listComponent: TechPortalFeatureOrderIssueListComponent,
      serializedName: 'OrderIssueListDto',
      idKey: 'issueId',
      entityService: OrderIssueService,
      entityType: AppEntityType.Issue,
      detailLink: '/issues/order',
      module: 'communication',
      providers: [
        {
          provide: OrderIssueService,
          useClass: OrderIssueService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.DirectMessageIssueControllerId]: {
      listComponent: TechPortalFeatureDirectMessageIssueListComponent,
      serializedName: 'DirectMessageIssueListDto',
      idKey: 'issueId',
      entityService: DirectMessageIssueService,
      entityType: AppEntityType.Issue,
      detailLink: '/issues/direct-message',
      module: 'communication',
      providers: [
        {
          provide: DirectMessageIssueService,
          useClass: DirectMessageIssueService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.MessageControlControllerId]: {
      listComponent: TechPortalFeatureMessageControlIssueListComponent,
      serializedName: 'MessageControlListDto',
      idKey: 'issueId',
      entityService: MessageControlIssueService,
      entityType: AppEntityType.Issue,
      detailLink: '/issues/message-control',
      module: 'communication',
      providers: [
        {
          provide: MessageControlIssueService,
          useClass: MessageControlIssueService,
          deps: [Injector]
        }
      ]
    },
    // TODO: ResourceIssueControllerId does not exist yet, but it is required for this datatable type
    // [ControllerOperationId.ResourceIssueControllerId]: {
    //   listComponent: ResourceIssuesComponent,
    //   listComponentProps: {
    //     allowMargin: true,
    //     isDashboardView: true
    //   },
    //   serializedName: 'IssueListDto',
    //   idKey: 'issueId',
    //   entityService: TechPortalFeatureResourceIssueService,
    //   entityType: AppEntityType.Issue,
    //   detailLink: '/resources',
    //   module: 'inventory',
    //   providers: [
    //     {
    //       provide: TechPortalFeatureResourceIssueService,
    //       useClass: TechPortalFeatureResourceIssueService,
    //       deps: [Injector]
    //     }
    //   ]
    // },
    [ControllerOperationId.TicketInLocationControllerId]: {
      listComponent: LocationTicketListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'TicketListDto',
      idKey: 'ticketId',
      detailLink: '/tickets',
      entityService: LocationTicketService,
      entityType: AppEntityType.Ticket,
      module: 'communication',
      providers: [
        {
          provide: LocationTicketService,
          useClass: LocationTicketService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.MissionReceiptControllerList]: {
      listComponent: TechPortalFeatureMissionReceiptsListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'MissionReceiptListDto',
      idKey: 'missionReceiptId',
      detailLink: '',
      entityService: TechPortalFeatureMissionReceiptsListService,
      entityType: AppEntityType.None,
      module: 'inventory',
      providers: [
        {
          provide: TechPortalFeatureMissionReceiptsListService,
          useClass: TechPortalFeatureMissionReceiptsListService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.TicketsInResourcesControllerList]: {
      listComponent: ResourceTicketsComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'TicketListDto',
      idKey: 'ticketId',
      detailLink: '/tickets',
      entityService: TechPortalFeatureResourceTicketService,
      entityType: AppEntityType.Ticket,
      module: 'communication',
      providers: [
        {
          provide: TechPortalFeatureResourceTicketService,
          useClass: TechPortalFeatureResourceTicketService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.MissionsInResourcesControllerList]: {
      listComponent: ResourceMissionsComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'MissionCompleteDto',
      idKey: 'missionId',
      detailLink: '/missions/all',
      entityService: TechPortalFeatureResourceMissionService,
      entityType: AppEntityType.Mission,
      module: 'inventory',
      providers: [
        {
          provide: TechPortalFeatureResourceMissionService,
          useClass: TechPortalFeatureResourceMissionService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.ConsumptionHistoryForResourceList]: {
      listComponent: ConnectedOccurredConsumptionsListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'OccurredConsumptionListDto',
      idKey: 'occurredConsumptionId',
      detailLink: null,
      entityService: TechPortalFeatureConnectedOccurredConsumptionsService,
      entityType: AppEntityType.None,
      module: 'inventory',
      providers: [
        {
          provide: TechPortalFeatureConnectedOccurredConsumptionsService,
          useClass: TechPortalFeatureConnectedOccurredConsumptionsService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.ErrorHistoryForResourceList]: {
      listComponent: ConnectedOccurredErrorsListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'OccurredErrorListDto',
      idKey: 'occurredErrorId',
      detailLink: null,
      entityService: TechPortalFeatureConnectedOccurredErrorsService,
      entityType: AppEntityType.None,
      module: 'inventory',
      providers: [
        {
          provide: TechPortalFeatureConnectedOccurredErrorsService,
          useClass: TechPortalFeatureConnectedOccurredErrorsService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.HintHistoryForResourceList]: {
      listComponent: ConnectedOccurredHintsListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'OccurredHintListDto',
      idKey: 'occurredHintId',
      detailLink: null,
      entityService: TechPortalFeatureConnectedOccurredHintsService,
      entityType: AppEntityType.None,
      module: 'inventory',
      providers: [
        {
          provide: TechPortalFeatureConnectedOccurredHintsService,
          useClass: TechPortalFeatureConnectedOccurredHintsService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.OccuredConsumptionProtocol]: {
      listComponent: ResourceControlProtocolConsumptionsComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'OccurredConsumptionProtocolListDto',
      idKey: 'consumption.consumptionCodeId',
      detailLink: '',
      entityService: TechPortalFeatureResourceControlProtocolConsumptionsService,
      entityType: AppEntityType.None,
      module: '',
      providers: [
        {
          provide: TechPortalFeatureResourceControlProtocolConsumptionsService,
          useClass: TechPortalFeatureResourceControlProtocolConsumptionsService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.OccuredErrorProtocol]: {
      listComponent: ResourceControlProtocolErrorsComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'OccurredErrorProtocolListDto',
      idKey: 'occurredConnectedErrorHistoryId',
      detailLink: '',
      entityService: TechPortalFeatureResourceControlProtocolErrorsService,
      entityType: AppEntityType.None,
      module: '',
      providers: [
        {
          provide: TechPortalFeatureResourceControlProtocolErrorsService,
          useClass: TechPortalFeatureResourceControlProtocolErrorsService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.KnowledgePathProgressFunctionControllerId]: {
      listComponent: PathsProgressFunctionListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'KnowledgePathProgressFunctionListDto',
      idKey: 'functionId',
      detailLink: '/masterdata/functions',
      entityService: PathsProgressFunctionService,
      entityType: AppEntityType.Function,
      module: 'management',
      providers: [
        {
          provide: PathsProgressFunctionService,
          useClass: PathsProgressFunctionService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.KnowledgePathProgressLocationControllerId]: {
      listComponent: PathsProgressLocationListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'KnowledgePathProgressLocationListDto',
      idKey: 'locationId',
      detailLink: '/masterdata/locations',
      entityService: PathsProgressLocationService,
      entityType: AppEntityType.Location,
      module: 'management',
      providers: [
        {
          provide: PathsProgressLocationService,
          useClass: PathsProgressLocationService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.KnowledgePathProgressContactControllerId]: {
      listComponent: PathsProgressContactListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'KnowledgePathProgressContactListDto',
      idKey: 'contactId',
      detailLink: '/masterdata/contacts',
      entityService: PathsProgressContactService,
      entityType: AppEntityType.Contact,
      module: 'management',
      providers: [
        {
          provide: PathsProgressContactService,
          useClass: PathsProgressContactService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.ResourceModelInManufacturerControllerId]: {
      listComponent: ModelListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'ResourceModelListDto',
      idKey: 'resourceModelId',
      detailLink: '/model',
      entityService: CorePortalManufacturerModelService,
      entityType: AppEntityType.None,
      module: 'settings',
      providers: [
        {
          provide: CorePortalManufacturerModelService,
          useClass: CorePortalManufacturerModelService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.ResourceControllerList]: {
      listComponent: TechPortalFeatureResourceListComponent,
      listComponentProps: {
        allowMargin: true,
        isDashboardView: true
      },
      serializedName: 'ResourceListDto',
      idKey: 'resourceId',
      entityService: TechPortalFeatureVirtualResourceService,
      entityType: AppEntityType.Resource,
      detailLink: '/tech-dashboard/resources',
      module: 'management',
      providers: [
        {
          provide: TechPortalFeatureVirtualResourceService,
          useClass: TechPortalFeatureVirtualResourceService,
          deps: [Injector]
        },
        {
          provide: TechPortalFeatureResourceTicketService,
          useClass: TechPortalFeatureResourceTicketService,
          deps: [Injector]
        },
        {
          provide: TechPortalFeatureResourceMissionService,
          useClass: TechPortalFeatureResourceMissionService,
          deps: [Injector]
        },
        {
          provide: CorePortalFeatureMasterDataLocationGroupService,
          useClass: CorePortalFeatureMasterDataLocationGroupService,
          deps: [Injector]
        },
        {
          provide: TechPortalFeatureResourceService,
          useClass: TechPortalFeatureResourceService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.MissionControllerList]: {
      listComponent: TechPortalFeatureMissionListComponent,
      mapComponent: MissionMapViewComponent,
      ganttComponent: TechPortalFeatureMissionGanttViewComponent,
      datatableStore: TechPortalFeatureDashboardMissionDatatableStore,
      actionsFacade: TechPortalFeatureDashboardMissionActionsFacade,
      serializedName: 'MissionCompleteDto',
      idKey: 'missionId',
      entityService: TechPortalFeatureMissionService,
      entityType: AppEntityType.Mission,
      detailLink: '/missions/all',
      module: 'inventory',
      providers: [
        {
          provide: CorePortalFeatureResourceService,
          useClass: CorePortalFeatureResourceService,
          deps: [Injector]
        },
        {
          provide: TechPortalFeatureResourceTaskService,
          useClass: TechPortalFeatureResourceTaskService,
          deps: [Injector]
        },
        {
          provide: CorePortalContactService,
          useClass: CorePortalContactService,
          deps: [Injector]
        },
        {
          provide: CorePortalFunctionService,
          useClass: CorePortalFunctionService,
          deps: [Injector]
        },
        {
          provide: CorePortalFeatureMasterDataLocationGroupService,
          useClass: CorePortalFeatureMasterDataLocationGroupService,
          deps: [Injector]
        },
      ]
    },
    [ControllerOperationId.ContractControllerList]: {
      listComponent: TechPortalFeatureContractListComponent,
      serializedName: 'ContractListDto',
      idKey: 'contractId',
      entityService: TechPortalFeatureContractService,
      entityType: AppEntityType.Contract,
      detailLink: '/contracts',
      module: 'inventory',
      providers: [
        {
          provide: TechPortalFeatureContractService,
          useClass: TechPortalFeatureContractService,
          deps: [Injector]
        }
      ]
    },
    [ControllerOperationId.LocationControllerList]: {
      listComponent: CorePortalFeatureMasterDataLocationListComponent,
      serializedName: 'LocationListDto',
      idKey: 'locationId',
      entityService: CorePortalFeatureMasterDataLocationService,
      entityType: AppEntityType.Location,
      detailLink: '/masterdata/locations',
      module: 'management',
      providers: [{
        provide: CorePortalFeatureMasterDataLocationService,
        useClass: CorePortalFeatureMasterDataLocationService,
        deps: [Injector]
      }]
    },
    [ControllerOperationId.CompanyControllerList]: {
      listComponent: CorePortalFeatureMasterDataCompanyListComponent,
      serializedName: 'CompanyListDto',
      idKey: 'companyId',
      entityService: CorePortalFeatureMasterDataCompanyService,
      entityType: AppEntityType.Company,
      detailLink: '/masterdata/companies',
      module: 'management',
      providers: [{
        provide: CorePortalFeatureMasterDataCompanyService,
        useClass: CorePortalFeatureMasterDataCompanyService,
        deps: [Injector]
      }]
    },
    [ControllerOperationId.OpenEditorRequestsList]: {
      listComponent: MissionMyRequestsComponent,
      serializedName: 'OpenEditorRequestOnContactListDto',
      idKey: 'missionId',
      entityService: TechPortalFeatureMissionMyRequestsService,
      entityType: AppEntityType.None,
      detailLink: '/missions/all',
      module: 'inventory',
      providers: [{
        provide: TechPortalFeatureMissionMyRequestsService,
        useClass: TechPortalFeatureMissionMyRequestsService,
        deps: [Injector]
      }]
    },
    [ControllerOperationId.MissionReportList]: {
      listComponent: MissionReportsListComponent,
      serializedName: 'MissionReportListDto',
      idKey: 'missionReportId',
      entityService: TechPortalFeatureMissionReportsListService,
      entityType: AppEntityType.MissionReport,
      detailLink: '/missions/all',
      module: 'inventory',
      providers: [{
        provide: TechPortalFeatureMissionReportsListService,
        useClass: TechPortalFeatureMissionReportsListService,
        deps: [Injector]
      }]
    }
  };
}
